import { useEffect, useRef } from "react";

import { isServerSide } from "@jti/fe-common";
import type { FeedbackTranslations } from "@jti/fe-common/config";
import { AUTH_ROUTE_PATHS } from "@jti/fe-common/routes/auth-routes";
import { useAuthHrefResolver } from "@jti/ui/providers/AuthRoutingProvider";

import { ContentFeedbackStatus } from "@/constants/feedback/contentFeedbackStatus";
import type { ContentFeedbackAction } from "@/utils/feedback/feedbackActions";
import { getContentFeedbackActions } from "@/utils/feedback/feedbackActions";
import {
  getFeedbackQuestion,
  getFeedbackStatusMessage,
  getSubmittedFeedbackContent,
} from "@/utils/feedback/feedbackPresentation";

import { useContentFeedbackState } from "./useContentFeedbackState";

const REFERENCE_TYPE = "video";

export type VideoFeedbackAction = ContentFeedbackAction;

export type UseVideoFeedbackOptions = {
  videoUrl: string;
  feedbackTranslations: FeedbackTranslations;
  authRegisterPageUrl: string;
  handleClose: () => void;
  confirmationDisplayMs?: number;
};

export type UseVideoFeedbackResult = {
  status: ReturnType<typeof useContentFeedbackState>["status"];
  isCheckingUser: boolean;
  isInitializing: boolean;
  question?: string;
  message?: string;
  actions: VideoFeedbackAction[];
  submittedMessage?: string;
  submittedRewardHtml?: string;
};

const DEFAULT_CONFIRMATION_DISPLAY_MS = 3000;

export const useVideoFeedback = ({
  videoUrl,
  feedbackTranslations,
  authRegisterPageUrl,
  handleClose,
  confirmationDisplayMs = DEFAULT_CONFIRMATION_DISPLAY_MS,
}: UseVideoFeedbackOptions): UseVideoFeedbackResult => {
  const resolveAuthHref = useAuthHrefResolver();
  const { status, isUserLoggedIn, isCheckingUser, isInitializing, submitFeedback, markSubmissionAsHandled } =
    useContentFeedbackState({
      contentUrl: videoUrl,
      referenceType: REFERENCE_TYPE,
      referenceName: "video",
    });
  const confirmationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleCloseRef = useRef(handleClose);

  handleCloseRef.current = handleClose;

  useEffect(() => {
    if (status !== ContentFeedbackStatus.JustSubmitted) {
      return;
    }

    confirmationTimerRef.current = setTimeout(() => {
      markSubmissionAsHandled();
      handleCloseRef.current();
    }, confirmationDisplayMs);

    return () => {
      if (confirmationTimerRef.current) {
        clearTimeout(confirmationTimerRef.current);
      }
    };
  }, [confirmationDisplayMs, markSubmissionAsHandled, status]);

  const message = getFeedbackStatusMessage({ status, feedbackTranslations });
  const loginHref = resolveAuthHref({
    route: AUTH_ROUTE_PATHS.login,
    search: isServerSide() ? undefined : { returnUrl: window.location.href },
  });

  const actions: VideoFeedbackAction[] = getContentFeedbackActions({
    isUserLoggedIn,
    feedbackTranslations,
    loginHref,
    authRegisterPageUrl,
    submitFeedback,
    referenceType: REFERENCE_TYPE,
  });

  const question = getFeedbackQuestion({ isUserLoggedIn, feedbackTranslations });
  const { submittedMessage, submittedRewardHtml } = getSubmittedFeedbackContent(feedbackTranslations);

  return {
    status,
    isCheckingUser,
    isInitializing,
    question,
    message,
    actions,
    submittedMessage,
    submittedRewardHtml,
  };
};
